import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Brain, Bot, Check, CloudRain, Glasses, Headset, LoaderCircle, PenLine, Sparkles, UserRound } from 'lucide-react';
import { deleteAllMemoriesApi } from '../../api/memory.api';
import { Switch } from '../../components/ui/Switch';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { fetchMemorySettings, patchMemorySettings } from '../../redux/slices/memorySlice';
import { classNames } from '../../utils/classNames';
import { ASSISTANT_PERSONALITIES, TONE_TAG_PRESETS } from './personalizationConstants';
import { ConfirmDangerDialog } from './ConfirmDangerDialog';
import { MemorySummaryModal } from './MemorySummaryModal';
import { SettingsActionButton, SettingsRow, SettingsSection, settingsControlClass } from './settingsUi';

const fieldClass =
  'w-full rounded-2xl border border-border bg-white/80 px-3.5 py-3 text-base text-text outline-none placeholder:text-muted/80 transition focus:border-accent md:text-sm dark:bg-panel-strong';

const PERSONALITY_META = {
  default: { icon: Sparkles, tone: 'from-sky-100/90 to-indigo-100/70 dark:from-sky-500/15 dark:to-indigo-500/10' },
  pessimistic: { icon: CloudRain, tone: 'from-slate-100 to-slate-200/80 dark:from-slate-500/20 dark:to-slate-800/40' },
  robot: { icon: Bot, tone: 'from-cyan-100/90 to-teal-100/70 dark:from-cyan-500/15 dark:to-teal-500/10' },
  listener: { icon: Headset, tone: 'from-amber-100/90 to-orange-100/70 dark:from-amber-500/15 dark:to-orange-500/10' },
  geek: { icon: Glasses, tone: 'from-violet-100/90 to-fuchsia-100/70 dark:from-violet-500/15 dark:to-fuchsia-500/10' }
};

export const PersonalizationPanel = () => {
  const { t } = useI18n();
  const dispatch = useAppDispatch();
  const { settings, loading, saving, error } = useAppSelector((state) => state.memory);
  const [moreTones, setMoreTones] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [draft, setDraft] = useState({
    customInstructions: '',
    preferredName: '',
    job: '',
    interests: ''
  });
  const saveTimer = useRef(null);

  useEffect(() => {
    dispatch(fetchMemorySettings());
  }, [dispatch]);

  useEffect(() => {
    setDraft({
      customInstructions: settings.customInstructions || '',
      preferredName: settings.preferredName || '',
      job: settings.job || '',
      interests: settings.interests || ''
    });
  }, [settings.customInstructions, settings.preferredName, settings.job, settings.interests]);

  const selectedPersonality = useMemo(
    () => ASSISTANT_PERSONALITIES.find((item) => item.id === settings.personality) || ASSISTANT_PERSONALITIES[0],
    [settings.personality]
  );

  const visibleTones = moreTones ? TONE_TAG_PRESETS : TONE_TAG_PRESETS.slice(0, 3);

  const queueSave = (patch) => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    saveTimer.current = setTimeout(() => {
      dispatch(patchMemorySettings(patch));
    }, 500);
  };

  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
    },
    []
  );

  const toggleTone = (id) => {
    const current = settings.toneTags || [];
    const next = current.includes(id) ? current.filter((tag) => tag !== id) : [...current, id];
    dispatch(patchMemorySettings({ toneTags: next }));
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await deleteAllMemoriesApi();
      await dispatch(fetchMemorySettings());
      setConfirmClear(false);
    } finally {
      setClearing(false);
    }
  };

  if (loading && !settings.updatedAt) {
    return (
      <div className="flex min-h-[280px] items-center justify-center gap-2 text-muted">
        <LoaderCircle size={22} className="animate-spin text-accent" aria-hidden />
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error ? (
        <p
          role="alert"
          className="rounded-2xl border border-rose-200/80 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200"
        >
          {error}
        </p>
      ) : null}

      <SettingsSection icon={Sparkles} title={t('choosePersonality')} description={t('choosePersonalityHint')}>
        <div className="grid gap-2 p-3 sm:grid-cols-2">
          {ASSISTANT_PERSONALITIES.map((item) => {
            const active = item.id === selectedPersonality.id;
            const meta = PERSONALITY_META[item.id] || PERSONALITY_META.default;
            const Icon = meta.icon;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => dispatch(patchMemorySettings({ personality: item.id }))}
                aria-pressed={active}
                className={classNames(
                  settingsControlClass,
                  'flex items-start gap-3 rounded-2xl border p-3 text-start',
                  active
                    ? 'border-accent bg-accent-soft/60 ring-2 ring-accent/20'
                    : 'border-border bg-white/60 hover:border-accent/35 dark:bg-slate-950/25'
                )}
              >
                <span
                  className={classNames(
                    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br',
                    meta.tone
                  )}
                >
                  <Icon size={18} className="text-accent" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-text">{t(item.labelKey)}</span>
                    {active ? <Check size={14} className="text-accent" aria-hidden /> : null}
                  </span>
                  <span className="mt-0.5 block text-xs leading-5 text-muted">{t(item.hintKey)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </SettingsSection>

      <SettingsSection icon={PenLine} title={t('instructions')}>
        <div className="space-y-3 px-4 py-4">
          {saving ? (
            <p className="text-end text-[11px] text-muted" aria-live="polite">
              {t('settingsSaving')}
            </p>
          ) : null}
          <textarea
            id="settings-instructions"
            aria-label={t('instructions')}
            value={draft.customInstructions}
            onChange={(event) => {
              const value = event.target.value;
              setDraft((current) => ({ ...current, customInstructions: value }));
              queueSave({ customInstructions: value });
            }}
            placeholder={t('instructionsPlaceholder')}
            className={classNames(fieldClass, 'min-h-[88px] resize-none')}
          />
          <div className="flex flex-wrap gap-2">
            {visibleTones.map((tone) => {
              const active = (settings.toneTags || []).includes(tone.id);
              return (
                <button
                  key={tone.id}
                  type="button"
                  onClick={() => toggleTone(tone.id)}
                  aria-pressed={active}
                  className={classNames(
                    settingsControlClass,
                    'rounded-full border px-3 py-1.5 text-xs font-medium',
                    active
                      ? 'border-accent bg-accent text-white dark:text-slate-950'
                      : 'border-border text-muted hover:border-accent/40 hover:text-text'
                  )}
                >
                  {t(tone.labelKey)}
                </button>
              );
            })}
            <button
              type="button"
              className={classNames(
                settingsControlClass,
                'rounded-full border border-dashed border-border px-3 py-1.5 text-xs text-muted hover:text-text'
              )}
              onClick={() => setMoreTones((value) => !value)}
              aria-expanded={moreTones}
              aria-label={t('moreTones')}
            >
              {moreTones ? '–' : '…'}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection icon={UserRound} title={t('aboutYou')}>
        <div className="space-y-3 px-4 py-4">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted">{t('aboutYouName')}</span>
            <input
              value={draft.preferredName}
              onChange={(event) => {
                const value = event.target.value;
                setDraft((current) => ({ ...current, preferredName: value }));
                queueSave({ preferredName: value });
              }}
              placeholder={t('aboutYouName')}
              className={fieldClass}
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted">{t('aboutYouJob')}</span>
            <input
              value={draft.job}
              onChange={(event) => {
                const value = event.target.value;
                setDraft((current) => ({ ...current, job: value }));
                queueSave({ job: value });
              }}
              placeholder={t('aboutYouJob')}
              className={fieldClass}
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted">{t('aboutYouInterests')}</span>
            <textarea
              value={draft.interests}
              onChange={(event) => {
                const value = event.target.value;
                setDraft((current) => ({ ...current, interests: value }));
                queueSave({ interests: value });
              }}
              placeholder={t('aboutYouInterests')}
              className={classNames(fieldClass, 'min-h-[80px] resize-none')}
            />
          </label>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Brain}
        title={t('memory')}
        description={t('memoryToggleHint')}
        action={
          <Switch
            checked={Boolean(settings.memoryEnabled)}
            onChange={(checked) => dispatch(patchMemorySettings({ memoryEnabled: checked }))}
            aria-label={t('memory')}
            disabled={saving}
          />
        }
      >
        {!settings.memoryEnabled ? (
          <p className="px-4 py-3 text-xs leading-5 text-muted">{t('memoryDisabledHint')}</p>
        ) : null}
        <SettingsRow title={t('memorySummaryTitle')} description={t('memorySummaryHint')} info={t('memoryTooltip')}>
          <SettingsActionButton onClick={() => setSummaryOpen(true)}>{t('manage')}</SettingsActionButton>
        </SettingsRow>
        <SettingsRow title={t('memoryClearTitle')} description={t('memoryClearHint')} danger>
          <SettingsActionButton danger onClick={() => setConfirmClear(true)}>
            {t('memoryClear')}
          </SettingsActionButton>
        </SettingsRow>
      </SettingsSection>

      <MemorySummaryModal open={summaryOpen} settings={settings} onClose={() => setSummaryOpen(false)} />
      <ConfirmDangerDialog
        open={confirmClear}
        title={t('memoryClearTitle')}
        message={t('memoryClearConfirm')}
        confirmLabel={t('memoryClear')}
        loading={clearing}
        onClose={() => setConfirmClear(false)}
        onConfirm={handleClear}
      />
    </div>
  );
};
